import { Grid } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import AddButton from "../../common/components/functional/addButton/AddButton";
import FormSection from "../../common/components/functional/formSection/FormSection";
import { ContentKey } from "../talentProfile/TalentDetails";
import { selectTalentById } from "../talents/talentsSlice";
import { Talent, talentIsDeactivated } from "../talents/talentAPI";
import { removeTalentWorkHistoryEntry } from "./WorkHistoriesSlice";
import WorkHistoryTable from "./WorkHistoryTable";
import { WorkHistory } from "./types/queries";
import {
  getLongestDateRange,
  getWorkLocationObject,
  isExistingWorkItem,
  sortWorkHistory,
} from "./utils";
import { canExpertEditTalentProfile } from "../user/accessRightChecks";

export type WorkLocation = {
  workHistoryId: string;
  workLocationName?: string;
  customerOrgName?: string;
  jobDescription?: string;
  startDate: string;
  endDate?: string;
  isEezyWork: boolean;
  editable?: boolean;
  deletable?: boolean;
};

export type FormattedWorkHistory = {
  workTitleRowId: string;
  workTitleId?: string | null;
  workTitleName?: string;
  startDate: string;
  endDate?: string;
  isEezyWork: boolean;
  workLocations: WorkLocation[];
  source: "Expert" | "System" | "Migration";
};

type Props = {
  workHistories: WorkHistory[];
  handleOpenEditModal: (content: ContentKey) => void;
  isPreviewMode?: boolean;
};

const TalentWorkHistory = ({
  workHistories,
  handleOpenEditModal,
  isPreviewMode = false,
}: Props) => {
  const { t } = useTranslation("workHistory");
  const { talentId } = useParams();
  const dispatch = useAppDispatch();

  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  let formattedWorkHistoryArray: FormattedWorkHistory[] = [];

  // Group workHistories data by is eezy work and work title to formattedWorkHistoryArray
  workHistories.forEach((item) => {
    const existingWork = formattedWorkHistoryArray.find((work) =>
      isExistingWorkItem(item, work),
    );

    if (!!existingWork) {
      // Add new work location to existing work item

      // Get updated workLocations array
      const newWorkLocationsArray = [
        ...existingWork.workLocations,
        getWorkLocationObject(item),
      ];

      // Get longest date range for workLocations
      const { startDate, endDate } = getLongestDateRange(newWorkLocationsArray);

      // Create updatedObject with new startDate, endDate and workLocations
      const updatedObject = {
        ...existingWork,
        startDate,
        endDate,
        workLocations: newWorkLocationsArray,
      };

      // Update formattedWorkHistoryArray with updatedObject
      formattedWorkHistoryArray = formattedWorkHistoryArray.map((work) =>
        work.workTitleRowId === existingWork.workTitleRowId
          ? updatedObject
          : work,
      );
    } else {
      // Create new work item and add it to formattedWorkHistoryArray
      const workTitleId = item.workTitleId || item.workTitleName;
      // If neither workTitleId or workTitleName does not exist, return
      if (!workTitleId) return;
      // We need different id for same work title depending on isEezyWork
      const workTitleRowId = `${workTitleId}_${
        item.isEezyWork ? "eezyWork" : "otherWork"
      }`;
      // Add new work item
      formattedWorkHistoryArray.push({
        workTitleRowId: workTitleRowId,
        workTitleId: item.workTitleId || null,
        ...(item.workTitleName && {
          workTitleName: item.workTitleName,
        }),
        startDate: item.startDate,
        endDate: item.endDate,
        isEezyWork: item.isEezyWork,
        workLocations: [getWorkLocationObject(item)],
        source: "Expert",
      });
    }
  });

  const handleDelete = (workHistoryId: string) => {
    const deletableWorkHistory = workHistories.find(
      (workHistory) => workHistory.id === workHistoryId,
    );

    if (deletableWorkHistory) {
      const commandPayload = {
        id: deletableWorkHistory.id,
        version: deletableWorkHistory.version,
      };

      dispatch(removeTalentWorkHistoryEntry(commandPayload)).then((action) =>
        isFulfilled(action),
      );
    }
  };

  // Sort work titles and work locations arrays
  const sortedFormattedWorkHistoryArray = sortWorkHistory(
    formattedWorkHistoryArray,
  );

  return !isPreviewMode ? (
    <FormSection title={t("workExperience")} margin>
      <Grid container columnSpacing={2}>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        />
        <Grid item xs={9} sx={{ overflowX: "auto" }}>
          <WorkHistoryTable
            data={sortedFormattedWorkHistoryArray}
            handleOpenEditModal={handleOpenEditModal}
            handleDelete={handleDelete}
          />
          <AddButton
            label={t("addWorkExperience")}
            onClick={() => handleOpenEditModal("workHistory_add")}
            accessRightChecks={[canExpertEditTalentProfile]}
            disabled={talentIsDeactivated(talent)}
          />
        </Grid>
      </Grid>
    </FormSection>
  ) : (
    <WorkHistoryTable
      data={sortedFormattedWorkHistoryArray}
      handleOpenEditModal={() => {}}
      handleDelete={() => {}}
      canEdit={!isPreviewMode}
    />
  );
};

export default TalentWorkHistory;
